import { useCallback } from 'react';
import { message } from 'antd';
import { useAtom } from 'jotai';
import {
  currentFormSchemaAtom,
  formMetadataAtom,
  uploadedSqliteFilesAtom,
  currentTemplateUuidAtom,
  formBuilderLoadingAtom,
  getDefaultFixedFields,
  ensureFixedSystemFields,
  isFixedSystemFieldId,
} from '~/stores/FormBuilderStore';
import { FormField_V2, FieldType_V2, FormSchema_V2 } from '../../../common/typescript/models/CollectFormV2';
import { CollectFormTemplateAPIs } from '../../../common/typescript/apis/CollectFormTemplateAPIs';
import { CollectFormAPIs } from '../../../common/typescript/apis/CollectFormAPIs';
import type { CollectForm_Base } from '../../../common/typescript/models/CollectForm';

export { getDefaultFixedFields, ensureFixedSystemFields, isFixedSystemFieldId };

export const FIELD_ID_REGEX = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

export interface FieldRowValidationError {
  rowIndex: number;
  labelError?: string;
  idError?: string;
}

export const validateFormFieldsDetailed = (fields: FormField_V2[]): {
  isValid: boolean;
  errors: string[];
  rowErrors: Map<number, FieldRowValidationError>;
} => {
  const errors: string[] = [];
  const rowErrors = new Map<number, FieldRowValidationError>();
  const firstSeenById = new Map<string, number>();

  // Pass 1: count occurrences of each normalized ID
  const idCounts = new Map<string, number>();
  fields.forEach((f) => {
    const norm = (f.id || '').trim().toLowerCase();
    if (norm) {
      idCounts.set(norm, (idCounts.get(norm) || 0) + 1);
    }
  });

  if (!idCounts.has('name')) {
    errors.push('Biểu mẫu bắt buộc phải có trường cố định "name" (Tên)');
  }
  if (!idCounts.has('description')) {
    errors.push('Biểu mẫu bắt buộc phải có trường cố định "description" (Mô tả)');
  }

  fields.forEach((field, index) => {
    const rawLabel = (field.label || '').trim();
    const rawId = (field.id || '').trim();
    const normId = rawId.toLowerCase();
    const rowErr: FieldRowValidationError = { rowIndex: index };

    if (!rawLabel) {
      rowErr.labelError = 'Vui lòng nhập tên trường';
      errors.push(`Trường #${index + 1}: Vui lòng nhập tên trường hiển thị`);
    }

    if (!rawId) {
      rowErr.idError = 'Vui lòng nhập mã trường';
      errors.push(`Trường #${index + 1}: Vui lòng nhập mã trường (ID)`);
    } else if (!FIELD_ID_REGEX.test(rawId)) {
      rowErr.idError = 'Chỉ dùng chữ không dấu, số và _';
      errors.push(
        `Trường #${index + 1}: Mã trường "${rawId}" không hợp lệ (chỉ dùng chữ cái không dấu, số và dấu gạch dưới, không bắt đầu bằng số)`,
      );
    } else if ((idCounts.get(normId) || 0) > 1) {
      const firstIdx = firstSeenById.get(normId);
      if (firstIdx !== undefined) {
        rowErr.idError = `Trùng mã với trường #${firstIdx + 1}`;
        errors.push(`Trường #${index + 1}: Mã trường "${rawId}" bị trùng với trường #${firstIdx + 1}`);
      } else {
        firstSeenById.set(normId, index);
        if (!isFixedSystemFieldId(normId)) {
          rowErr.idError = 'Trùng mã trường';
        }
      }
    } else {
      firstSeenById.set(normId, index);
    }

    if (rowErr.labelError || rowErr.idError) {
      rowErrors.set(index, rowErr);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    rowErrors,
  };
};

export const useFormBuilder = () => {
  const [formSchema, setFormSchema] = useAtom(currentFormSchemaAtom);
  const [metadata, setMetadata] = useAtom(formMetadataAtom);
  const [uploadedFiles, setUploadedFiles] = useAtom(uploadedSqliteFilesAtom);
  const [templateUuid, setTemplateUuid] = useAtom(currentTemplateUuidAtom);
  const [loading, setLoading] = useAtom(formBuilderLoadingAtom);

  // Add field to form
  const addField = useCallback(
    (field: FormField_V2) => {
      setFormSchema((prev) => ({
        ...prev,
        fields: [...prev.fields, field],
      }));
    },
    [setFormSchema],
  );

  // Update existing field by ID
  const updateField = useCallback(
    (fieldId: string, updatedField: FormField_V2) => {
      setFormSchema((prev) => ({
        ...prev,
        fields: prev.fields.map((f) => (f.id === fieldId ? updatedField : f)),
      }));
    },
    [setFormSchema],
  );

  // Update existing field by row index (safe when editing IDs inline or when duplicate IDs exist)
  const updateFieldAtIndex = useCallback(
    (index: number, updatedField: FormField_V2) => {
      setFormSchema((prev) => ({
        ...prev,
        fields: prev.fields.map((f, idx) => (idx === index ? updatedField : f)),
      }));
    },
    [setFormSchema],
  );

  // Delete field by ID
  const deleteField = useCallback(
    (fieldId: string) => {
      if (isFixedSystemFieldId(fieldId)) {
        message.warning(`Trường cố định "${fieldId}" không thể xóa`);
        return;
      }
      setFormSchema((prev) => ({
        ...prev,
        fields: prev.fields.filter((f) => f.id !== fieldId),
      }));
    },
    [setFormSchema],
  );

  // Delete field by row index
  const deleteFieldAtIndex = useCallback(
    (index: number) => {
      setFormSchema((prev) => {
        const target = prev.fields[index];
        if (target && isFixedSystemFieldId(target.id)) {
          const norm = target.id.trim().toLowerCase();
          const firstIdx = prev.fields.findIndex((f) => (f.id || '').trim().toLowerCase() === norm);
          if (firstIdx === index) {
            message.warning(`Trường cố định "${target.id}" không thể xóa`);
            return prev;
          }
        }
        return {
          ...prev,
          fields: prev.fields.filter((_, idx) => idx !== index),
        };
      });
    },
    [setFormSchema],
  );

  // Reorder fields
  const reorderFields = useCallback(
    (fromIndex: number, toIndex: number) => {
      setFormSchema((prev) => {
        const newFields = [...prev.fields];
        const [removed] = newFields.splice(fromIndex, 1);
        newFields.splice(toIndex, 0, removed);
        return {
          ...prev,
          fields: newFields,
        };
      });
    },
    [setFormSchema],
  );

  // Create new field from type
  const createFieldFromType = useCallback((type: FieldType_V2): FormField_V2 => {
    const id = `field_${Date.now()}`;
    return {
      id,
      label: `New ${type} Field`,
      type,
      required: false,
      placeholder: '',
    };
  }, []);

  // Save template
  const saveTemplate = useCallback(async () => {
    if (!metadata.name.trim()) {
      message.error('Vui lòng nhập tên biểu mẫu');
      return false;
    }

    if (formSchema.fields.length === 0) {
      message.error('Vui lòng thêm ít nhất một trường dữ liệu');
      return false;
    }

    const validation = validateFormFieldsDetailed(formSchema.fields);
    if (!validation.isValid) {
      message.error(validation.errors[0]);
      return false;
    }

    try {
      setLoading(true);

      const sanitizedSchema: FormSchema_V2 = {
        ...formSchema,
        fields: formSchema.fields.map((f) => ({
          ...f,
          id: f.id.trim(),
          label: f.label.trim(),
        })),
      };

      const templateData = {
        name: metadata.name.trim(),
        description: metadata.description.trim(),
        content: sanitizedSchema,
        sqlite_files: uploadedFiles.length > 0 ? uploadedFiles.join(',') : undefined,
      };

      if (templateUuid) {
        // Update existing
        await CollectFormTemplateAPIs.remote_updateTemplate(templateUuid, templateData);
        message.success('Đã cập nhật template biểu mẫu thành công');
      } else {
        // Create new
        const response = await CollectFormTemplateAPIs.remote_createTemplate(templateData);
        setTemplateUuid(response.data.uuid);
        message.success('Đã tạo template biểu mẫu thành công');
      }

      return true;
    } catch (error) {
      console.error('[useFormBuilder] Save failed:', error);
      message.error('Không thể lưu template biểu mẫu');
      return false;
    } finally {
      setLoading(false);
    }
  }, [metadata, formSchema, uploadedFiles, templateUuid, setLoading, setTemplateUuid]);

  // Save Project Collect Form V2
  const saveProjectForm = useCallback(
    async (projectId?: number | null, existingFormUuid?: string | null): Promise<CollectForm_Base | null> => {
      if (!projectId) {
        message.error('Vui lòng chọn dự án cho biểu mẫu');
        return null;
      }

      if (!metadata.name.trim()) {
        message.error('Vui lòng nhập tên biểu mẫu');
        return null;
      }

      if (formSchema.fields.length === 0) {
        message.error('Vui lòng thêm ít nhất một trường dữ liệu');
        return null;
      }

      const validation = validateFormFieldsDetailed(formSchema.fields);
      if (!validation.isValid) {
        message.error(validation.errors[0]);
        return null;
      }

      try {
        setLoading(true);

        const schemaToSave: FormSchema_V2 = {
          ...formSchema,
          fields: formSchema.fields.map((f) => ({
            ...f,
            id: f.id.trim(),
            label: f.label.trim(),
          })),
          metadata: {
            ...(formSchema.metadata || {}),
            version: '2.0.0',
            createdWith: 'GeoCloudWeb',
          },
        };

        const name = metadata.name.trim();
        const description = metadata.description.trim() || name;
        const contentStr = JSON.stringify(schemaToSave);
        const targetUuid = existingFormUuid || templateUuid;

        if (targetUuid) {
          const response = await CollectFormAPIs.remote_updateCollectForm(targetUuid, {
            name,
            description,
            content: contentStr,
            form_version: 2,
          } as any);
          message.success('Đã cập nhật biểu mẫu thành công');
          return response.data;
        } else {
          const response = await CollectFormAPIs.remote_createCollectForm({
            name,
            description,
            content: contentStr,
            projectId: Number(projectId),
            form_version: 2,
          } as any);
          if (response.data?.uuid) {
            setTemplateUuid(response.data.uuid);
          }
          message.success('Đã tạo biểu mẫu trong dự án thành công');
          return response.data;
        }
      } catch (error: any) {
        console.error('[useFormBuilder] Save project form failed:', error);
        const errMsg =
          error?.response?.data?.i18n?.vi ||
          error?.response?.data?.error?.i18n?.vi ||
          error?.response?.data?.message ||
          'Không thể lưu biểu mẫu vào dự án';
        message.error(errMsg);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [metadata, formSchema, templateUuid, setLoading, setTemplateUuid],
  );

  // Load template
  const loadTemplate = useCallback(
    async (uuid: string) => {
      try {
        setLoading(true);
        const template = await CollectFormTemplateAPIs.remote_getOneTemplate(uuid);

        setMetadata({
          name: template.name,
          description: template.description || '',
        });
        setFormSchema({
          ...template.content,
          fields: ensureFixedSystemFields(template.content?.fields || []),
        });
        setTemplateUuid(template.uuid);

        if (template.sqlite_files) {
          setUploadedFiles(template.sqlite_files.split(','));
        }

        message.success('Đã tải template biểu mẫu');
      } catch (error) {
        console.error('[useFormBuilder] Load failed:', error);
        message.error('Không thể tải template biểu mẫu');
      } finally {
        setLoading(false);
      }
    },
    [setLoading, setMetadata, setFormSchema, setTemplateUuid, setUploadedFiles],
  );

  // Load Project Collect Form (converts V1 to V2 if needed, or loads V2 directly)
  const loadProjectForm = useCallback(
    async (formUuid: string): Promise<CollectForm_Base | null> => {
      try {
        setLoading(true);
        const form = await CollectFormAPIs.remote_getOneCollectForm(formUuid);

        setMetadata({
          name: form.name || '',
          description: form.description || '',
        });
        setTemplateUuid(form.uuid);

        let parsedContent: any = form.content;
        if (typeof parsedContent === 'string') {
          try {
            parsedContent = JSON.parse(parsedContent);
          } catch (e) {
            console.warn('[useFormBuilder] Could not parse form content JSON:', e);
            parsedContent = null;
          }
        }

        if (parsedContent && typeof parsedContent === 'object' && !Array.isArray(parsedContent) && Array.isArray(parsedContent.fields)) {
          // V2 schema
          setFormSchema({
            ...parsedContent,
            fields: ensureFixedSystemFields(parsedContent.fields),
            metadata: {
              ...(parsedContent.metadata || {}),
              version: '2.0.0',
              createdWith: 'GeoCloudWeb',
            },
          });
        } else if (Array.isArray(parsedContent)) {
          // Legacy V1 array -> upgrade to V2 schema
          const upgradedFields: FormField_V2[] = parsedContent.map((f: any, idx: number) => ({
            id: f.key || f.id || `field_${idx + 1}`,
            label: f.label || f.key || `Trường ${idx + 1}`,
            type: (f.type as FieldType_V2) || FieldType_V2.TEXT,
            required: Boolean(f.required),
            config: Array.isArray(f.options)
              ? {
                  options: f.options.map((opt: any) =>
                    typeof opt === 'string' ? { value: opt, label: opt } : opt,
                  ),
                }
              : undefined,
          }));
          setFormSchema({
            fields: ensureFixedSystemFields(upgradedFields),
            metadata: {
              version: '2.0.0',
              createdWith: 'GeoCloudWeb',
            },
          });
        } else {
          setFormSchema({
            fields: getDefaultFixedFields(),
            metadata: {
              version: '2.0.0',
              createdWith: 'GeoCloudWeb',
            },
          });
        }

        return form;
      } catch (error) {
        console.error('[useFormBuilder] Load project form failed:', error);
        message.error('Không thể tải thông tin biểu mẫu dự án');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [setLoading, setMetadata, setFormSchema, setTemplateUuid],
  );

  // Reset form
  const resetForm = useCallback(() => {
    setFormSchema({
      fields: getDefaultFixedFields(),
      metadata: {
        version: '2.0.0',
        createdWith: 'GeoCloudWeb',
      },
    });
    setMetadata({ name: '', description: '' });
    setUploadedFiles([]);
    setTemplateUuid(null);
  }, [setFormSchema, setMetadata, setUploadedFiles, setTemplateUuid]);

  // Section Management
  const addSection = useCallback(
    (section: any) => {
      setFormSchema((prev) => ({
        ...prev,
        sections: [...(prev.sections || []), section],
      }));
    },
    [setFormSchema],
  );

  const updateSection = useCallback(
    (sectionId: string, updatedSection: any) => {
      setFormSchema((prev) => ({
        ...prev,
        sections: (prev.sections || []).map((s) => (s.id === sectionId ? updatedSection : s)),
      }));
    },
    [setFormSchema],
  );

  const deleteSection = useCallback(
    (sectionId: string) => {
      setFormSchema((prev) => ({
        ...prev,
        // Remove section
        sections: (prev.sections || []).filter((s) => s.id !== sectionId),
        // Reset section property for fields in this section
        fields: prev.fields.map((f) => (f.section === sectionId ? { ...f, section: undefined } : f)),
      }));
    },
    [setFormSchema],
  );

  return {
    formSchema,
    metadata,
    uploadedFiles,
    templateUuid,
    loading,
    addField,
    updateField,
    updateFieldAtIndex,
    deleteField,
    deleteFieldAtIndex,
    reorderFields,
    createFieldFromType,
    saveTemplate,
    saveProjectForm,
    loadTemplate,
    loadProjectForm,
    resetForm,
    setMetadata,
    setUploadedFiles,
    setFormSchema,
    setLoading,
    addSection,
    updateSection,
    deleteSection,
  };
};
